import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { ArrowLeft, Search, ChevronRight } from "lucide-react";
import { CATEGORY_CONFIG_MINIMAL, IconAssistant, IconShield } from "../components/MinimalIcons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DiscoveryPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories`);
        if (response.ok) setCategories(await response.json());
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="discovery-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-4">
          <button 
            onClick={() => navigate("/client")}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors touch-target"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" strokeWidth={1.5} />
          </button>
          <span className="font-medium text-neutral-900 tracking-tight">Serviços</span>
        </div>
      </header>

      <main className="px-5 py-8 space-y-8">
        {/* Title */}
        <div className="animate-up">
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">
            O que você precisa?
          </h1>
          <p className="text-neutral-500 mt-1 text-sm">
            Escolha um serviço ou fale com nossa IA
          </p>
        </div>

        {/* Search */}
        <div className="input-with-icon animate-up stagger-1">
          <Search className="input-icon w-5 h-5" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Buscar serviço..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-minimal"
            data-testid="search-input"
          />
        </div>

        {/* Quick Actions - Minimal */}
        <div className="grid grid-cols-2 gap-3 animate-up stagger-2">
          <button
            onClick={() => navigate("/assistant")}
            className="p-4 bg-neutral-900 rounded-2xl text-left"
            data-testid="ai-assistant-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-3">
              <IconAssistant className="w-5 h-5 text-white" />
            </div>
            <p className="font-medium text-white text-sm">Assistente</p>
            <p className="text-neutral-400 text-xs mt-0.5">Diagnóstico com IA</p>
          </button>

          <button
            onClick={() => navigate("/direct-provider")}
            className="p-4 bg-neutral-100 rounded-2xl text-left"
            data-testid="direct-provider-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-3">
              <IconShield className="w-5 h-5 text-neutral-700" />
            </div>
            <p className="font-medium text-neutral-900 text-sm">Meu profissional</p>
            <p className="text-neutral-500 text-xs mt-0.5">Pagamento protegido</p>
          </button>
        </div>

        {/* Categories - Clean List */}
        <div className="animate-up stagger-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-neutral-900">Categorias</h2>
            <span className="text-neutral-400 text-xs">{filteredCategories.length} serviços</span>
          </div>

          <div className="space-y-2">
            {filteredCategories.map((category) => {
              const config = CATEGORY_CONFIG_MINIMAL[category.category_id] || CATEGORY_CONFIG_MINIMAL.cat_diarista;
              const Icon = config.Icon;
              
              return (
                <button
                  key={category.category_id}
                  onClick={() => navigate(`/diagnostic/${category.category_id}`)}
                  className="w-full flex items-center gap-4 p-4 bg-white border border-neutral-100 rounded-2xl hover:border-neutral-200 transition-all"
                  data-testid={`category-card-${category.category_id}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 ${config.text}`} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-neutral-900">{category.name}</p>
                      {category.supports_recurrence && (
                        <span className="px-2 py-0.5 bg-neutral-100 text-neutral-500 text-[10px] font-medium rounded-full uppercase tracking-wide">
                          Recorrente
                        </span>
                      )}
                    </div>
                    <p className="text-neutral-500 text-sm mt-0.5 truncate">{category.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-neutral-300 flex-shrink-0" strokeWidth={1.5} />
                </button>
              );
            })}
          </div>
        </div>

        {filteredCategories.length === 0 && (
          <div className="p-8 bg-neutral-50 rounded-2xl text-center">
            <p className="text-neutral-500 text-sm">Nenhum resultado para "{searchQuery}"</p>
            <button 
              onClick={() => setSearchQuery("")}
              className="text-neutral-900 font-medium text-sm mt-2 hover:underline"
            >
              Limpar busca
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
